import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { VOYAGE_PORT_CALLS_CREDITS } from 'src/app/services/operators/voyageradar-credit-actions';
import { VoyageTrackController } from './voyage-track.controller';

/**
 * Voyage Radar port-call controller — drives the anchor button and its drawer.
 *
 * Owns exactly one fact: the port-call rows currently on show, and which hull
 * they belong to. The map controller owns Leaflet and owns the HTTP call; both
 * actions are callbacks it assigns in `attach()` (the `onDimOthersToggle`
 * pattern), so this file never imports a map type.
 *
 * DELIBERATELY NOT a `PlaybackController`. Its two siblings extend that base
 * because the toolbar host hands them out as `context.controller`; this one is
 * injected straight into its control component and is never registered as a
 * host controller, so the fourteen no-op transport methods the base demands
 * would buy nothing. It also could not satisfy the base honestly: `state$` is
 * `PortCallsState`, not `PlaybackState`.
 *
 * The one thing it does that `VoyageVesselController` does not is subscribe to
 * `VoyageTrackController` itself — see the constructor.
 */

/** One row of the drawer's timeline, already shaped for display. */
export interface PortCallRow {
  /** portName, or portId, or 'Unknown port' — never blank. */
  portName: string;
  portCountry: string | null;
  arrival: string | null; // ISO
  departure: string | null; // ISO
  /**
   * The vessel is STILL THERE.
   *
   * A missing `departureTime` is not enough to conclude this, which is what the
   * first version got wrong. Real data shows plenty of rows with
   * `status: "departed"` and no `departureTime` — the operator knows the vessel
   * left but not when — and those rendered as "in port · since 7 Apr", which is
   * the opposite of what happened. The status is the authority on whether the
   * vessel is there; the timestamp only ever says *when*.
   */
  inPort: boolean;
  /** '2 d' / '14 h' alongside, or null when it cannot be computed. */
  stay: string | null;
  /**
   * Rendered as plain text, never mapped through a fixed set of labels. Only
   * `"berthed"` has ever been observed, so an unseen value must degrade to
   * whatever the operator said rather than vanish.
   */
  status: string | null;
}

export interface PortCallsState {
  /**
   * The hull this drawer is ABOUT — its title, its empty states and its fetch
   * all key off this, not off `VoyageTrackController.activeMmsi`.
   *
   * It used to read the active vessel directly, and that was a bug: FOUR
   * layers open a vessel popup (live and history bind their own in the host,
   * playback builds one per click, and one is the map controller's), and only
   * the playback one calls `activateVessel`. Clicking port calls on a live
   * snapshot hull therefore found no active vessel and silently bought
   * nothing. The subject follows the active vessel whenever that changes, and
   * can also be named outright by a caller that knows whose card was clicked.
   */
  subjectMmsi: string | null;
  subjectName: string | null;
  /**
   * MMSI the current `rows` belong to, or null before any lookup for this
   * vessel — compared against `subjectMmsi` to tell "held" from "not fetched".
   */
  fetchedForMmsi: string | null;
  rows: PortCallRow[];
  /** The operator's own total, or null for "unknown" — never rows.length. */
  total: number | null;
  /** More rows exist upstream than were bought. Drives "showing N of M". */
  truncated: boolean;
  /** `fetchedAt` of the stored record, for "fetched 6 d ago". */
  fetchedAt: string | null;
  portCallsLoading: boolean;
  /**
   * Price and balance live HERE, pushed by the map controller, because the
   * control cannot reach a sensor: `ToolbarControlContext` is
   * `{ controller, slot, options }` and carries no sensor row. This is the same
   * route `VoyageTrackController.setCreditsRemaining` already takes, so an
   * ee-admin re-price reaches the anchor button exactly as it reaches Trace.
   */
  portCallCredits: number;
  creditsRemaining: number | null;
  /** TOI over — nothing here can be bought again. See `hasVoyageToiFinished`. */
  toiEnded: boolean;
}

export const EMPTY_PORT_CALLS_STATE: PortCallsState = {
  subjectMmsi: null,
  subjectName: null,
  fetchedForMmsi: null,
  rows: [],
  total: null,
  truncated: false,
  fetchedAt: null,
  portCallsLoading: false,
  portCallCredits: VOYAGE_PORT_CALLS_CREDITS,
  creditsRemaining: null,
  toiEnded: false,
};

const HOUR_MS = 3600_000;
const DAY_MS = 24 * HOUR_MS;

const iso = (v: any): string | null => {
  if (!v) return null;
  const t = Date.parse(String(v));
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
};

/**
 * Whether the operator has said this vessel left.
 *
 * Only `departed` is recognised, because that is the only negative value real
 * responses have shown. Anything else — `berthed`, `anchored`, or a value
 * nobody has seen yet — is NOT treated as a departure: an unknown status must
 * not silently turn into a claim about where a ship is.
 */
function isDepartedStatus(status: string | null): boolean {
  return (status ?? '').trim().toLowerCase() === 'departed';
}

/**
 * How long the vessel stayed, in the coarsest unit that is still true: days
 * once there is at least one, hours below that. Null whenever it cannot be
 * computed — a call still in port has no end, and an unparseable timestamp
 * must not become "0 h".
 */
function stayFor(arrival: string | null, departure: string | null): string | null {
  if (!arrival || !departure) return null;
  const ms = Date.parse(departure) - Date.parse(arrival);
  if (!Number.isFinite(ms) || ms < 0) return null;
  if (ms >= DAY_MS) {
    const d = Math.round(ms / DAY_MS);
    return `${d} d`;
  }
  const h = Math.max(1, Math.round(ms / HOUR_MS));
  return `${h} h`;
}

/**
 * Stored `calls[]` → display rows, newest first.
 *
 * Pure, and exported so the panel's spec can exercise the row states without
 * standing up a controller. Sorted here rather than trusted from upstream: the
 * operator documents no order, and a timeline that is not chronological reads
 * as wrong data rather than as an unsorted list.
 */
export function buildPortCallRows(calls: any[]): PortCallRow[] {
  return (Array.isArray(calls) ? calls : [])
    .map((c: any) => {
      const arrival = iso(c?.arrivalTime) ?? iso(c?.enteredBoundary);
      const departure = iso(c?.departureTime);
      const status = c?.status != null ? String(c.status) : null;
      return {
        portName: String(c?.portName || c?.portId || 'Unknown port'),
        portCountry: c?.portCountry != null ? String(c.portCountry) : null,
        arrival,
        departure,
        // Still there only if the operator has not said otherwise AND there is
        // no departure stamp. `departed` is the one status treated as a
        // negative answer — the enum is NOT closed (see §0 of the plan), so
        // every other value falls through to the timestamp rather than being
        // guessed at.
        inPort: !departure && !isDepartedStatus(status),
        stay: stayFor(arrival, departure),
        status,
      };
    })
    .sort((a, b) => (Date.parse(b.arrival || '') || 0) - (Date.parse(a.arrival || '') || 0));
}

@Injectable({ providedIn: 'root' })
export class VoyagePortCallsController {
  private _state$ = new BehaviorSubject<PortCallsState>({ ...EMPTY_PORT_CALLS_STATE });
  readonly state$: Observable<PortCallsState> = this._state$.asObservable();

  constructor(private track: VoyageTrackController) {
    // THIS is how a vessel becomes the drawer's subject, and why no caller has
    // to remember to trigger it. `VoyageTrackController` is what every marker
    // click already updates — it is what raises the Trace button — so port calls
    // rides the exact same selection event rather than introducing a second
    // one. A hull selected anywhere on the map, with this drawer open or shut,
    // is picked up here.
    //
    // A change to a DIFFERENT mmsi always resets the rows; re-selecting the
    // SAME one is a no-op, so an in-flight fetch is never interrupted by its
    // own marker staying selected.
    this.track.state$.subscribe((ts) =>
      this.setSubject(ts.activeMmsi ?? null, ts.activeVesselName ?? null),
    );
  }

  /**
   * Point the drawer at a hull and show whatever is already held for it.
   *
   * The stored read is FREE — it looks at the record already sitting on the
   * sensor document, no request — which is what makes re-opening a bought
   * history cost nothing and need no click.
   *
   * Re-pointing at the SAME hull is a no-op, so an in-flight fetch is never
   * interrupted by its own marker staying selected.
   */
  setSubject(mmsi: string | null, name: string | null): void {
    const current = this._state$.value;
    if (mmsi === current.subjectMmsi) {
      // Same hull — keep the rows, but take a better name if one turned up.
      // A popup click knows the MMSI before it knows the name; selecting the
      // same vessel afterwards is what supplies it.
      if (name && name !== current.subjectName) this.patch({ subjectName: name });
      return;
    }
    const stored = mmsi ? this.onLookupStored?.(mmsi) : null;
    this.patch({
      ...EMPTY_PORT_CALLS_STATE,
      ...this.creditsOnly(),
      subjectMmsi: mmsi,
      subjectName: name,
      ...(stored ? this.rowsFromRecord(stored, mmsi) : {}),
    });
  }

  // ── Callbacks the map controller assigns in attach() ────────────────────────

  /** Read `sensor.portCallHistory` for this mmsi, or null. Free — no request. */
  onLookupStored: ((mmsi: string) => any | null) | null = null;

  /** The paid fetch itself — the map controller owns the HTTP call. */
  onFetch: ((mmsi: string, vesselName: string | null) => void) | null = null;

  /**
   * Assigned by whichever `PortCallControlComponent` is currently on screen, so
   * the vessel popup's anchor icon can open this drawer. The vessel is already
   * the active one by the time this fires — opening its popup is what made it
   * active — so it needs no mmsi argument.
   */
  onRequestOpen: (() => void) | null = null;

  // ── Actions ─────────────────────────────────────────────────────────────────

  /**
   * The vessel popup's anchor icon: point the drawer at this hull, open it, and
   * fetch — one click.
   *
   * `mmsi` is passed EXPLICITLY, from the clicked card's own `data-mmsi`, and
   * that is the whole fix for the bug this replaced: the old version read
   * `VoyageTrackController.activeMmsi`, which is null for every popup except
   * the playback layer's, so the button opened the drawer and bought nothing.
   * The card knows whose it is; nothing else has to agree.
   *
   * Always fetches, whether or not this hull has a stored record. A stored
   * record is a snapshot of what the operator knew when it was taken, and a
   * user pressing a button labelled `1 credit` is asking about the vessel now —
   * quietly returning a week-old answer because one exists is the wrong default
   * for a priced control. Free re-opening still happens on its own, whenever
   * the selection changes: that path costs nothing and needs no click.
   *
   * Two guards, and only two:
   *
   * - `onRequestOpen` null — no control is mounted, so there is no drawer to
   *   show the answer in. Spending with no visible result is worse than a
   *   button that does not respond.
   * - `portCallsLoading` — a request is already in flight, so a double-click
   *   cannot buy the same history twice.
   */
  openAndFetch(mmsi?: string | null, vesselName?: string | null): void {
    if (!this.onRequestOpen) return;

    // Before opening, so the drawer is already titled for the right hull as it
    // appears — and so a stored record is on screen while the refetch runs.
    const subject = mmsi ?? this.track.snapshot().activeMmsi ?? null;
    this.setSubject(subject, vesselName ?? this.subjectNameFor(subject));

    this.onRequestOpen();

    const state = this._state$.value;
    if (!state.subjectMmsi || state.portCallsLoading) return;
    this.fetch(state.subjectMmsi, state.subjectName);
  }

  /** Keep the name already held for this hull rather than blanking the title. */
  private subjectNameFor(mmsi: string | null): string | null {
    const s = this._state$.value;
    if (mmsi && s.subjectMmsi === mmsi) return s.subjectName;
    const active = this.track.snapshot();
    return active.activeMmsi === mmsi ? active.activeVesselName ?? null : null;
  }

  /** The 1-credit spend. Fires on click; the cost is on the control (rule R2). */
  fetch(mmsi: string | null | undefined, vesselName: string | null | undefined): void {
    if (!mmsi) return;
    this.patch({ portCallsLoading: true });
    // `?? null` rather than widening the callback: the map controller's
    // `vesselName` is a value it may store, and "absent" and "not known" are
    // the same thing to it — one of them should not reach the request body.
    this.onFetch?.(mmsi, vesselName ?? null);
  }

  /** Called by the map controller once a fetch resolves — success or empty. */
  setFetched(mmsi: string, record: any): void {
    this.patch({ portCallsLoading: false, ...this.rowsFromRecord(record, mmsi) });
  }

  setFailed(): void {
    this.patch({ portCallsLoading: false });
  }

  /**
   * Pushed by the map controller — the same job `setCreditsRemaining` does on
   * `VoyageTrackController`, and the reason the control needs no sensor.
   */
  setCredits(
    creditsRemaining: number | null,
    portCallCredits: number,
    toiEnded: boolean,
  ): void {
    this.patch({ creditsRemaining, portCallCredits, toiEnded });
  }

  snapshot(): PortCallsState {
    return this._state$.value;
  }

  // ── Internals ───────────────────────────────────────────────────────────────

  /**
   * An empty record is still an ANSWER for this hull — `fetchedForMmsi` is set
   * either way, so the drawer says "the operator has no history" rather than
   * dropping back to "not fetched yet" and re-offering a spend that already
   * happened for nothing.
   */
  private rowsFromRecord(record: any, mmsi: string | null | undefined): Partial<PortCallsState> {
    // The operator's own `shipName`, which the route stored on the record,
    // outranks whatever the map had on hand — it is the vessel naming itself.
    // Only when it said something: a null must not blank a working title.
    const named = record?.vesselName ? String(record.vesselName) : null;
    return {
      fetchedForMmsi: mmsi ?? (record?.mmsi != null ? String(record.mmsi) : null),
      rows: buildPortCallRows(record?.calls || []),
      total: Number.isFinite(record?.total) ? record.total : null,
      truncated: record?.truncated === true,
      fetchedAt: record?.name ?? null,
      ...(named ? { subjectName: named } : {}),
    };
  }

  /** Price/balance survive a reset — they belong to the sensor, not the hull. */
  private creditsOnly(): Partial<PortCallsState> {
    const { portCallCredits, creditsRemaining } = this._state$.value;
    return { portCallCredits, creditsRemaining };
  }

  private patch(patch: Partial<PortCallsState>): void {
    this._state$.next({ ...this._state$.value, ...patch });
  }
}
